/**
 * BottomSheet — slides up from the bottom of the screen.
 * Used for forms, pickers, and confirmations.
 * Swipe down to dismiss, max height 80vh.
 */

import { useEffect, useRef, type ReactNode } from 'react';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

export function BottomSheet({ open, onClose, title, children }: Readonly<BottomSheetProps>) {
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="bottomsheet-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }} onKeyDown={e => { if (e.key === 'Escape') onClose(); }} role="dialog" aria-modal="true" aria-label={title}>
      <div className="bottomsheet">
        <div className="bottomsheet__handle" />
        <div className="bottomsheet__header">
          {title && <h3 className="bottomsheet__title">{title}</h3>}
          <button type="button" className="bottomsheet__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="bottomsheet__content">
          {children}
        </div>
      </div>
    </div>
  );
}
